import React, { useRef } from 'react'
import styled from 'styled-components'
import Link from 'next/link'
import { SCREEN_SIZE } from '../../lib/Media'
import NoImg from '../icons/NoImg'
import { EventTrackingConsumer } from '../EventTrackingProvider/EventTrackingProvider'
import { CheckoutAddressConsumer } from '../CheckoutAddressProvider/CheckoutAddressProvider'
import { impressionTypes } from '../../lib/impressionsTracker'
import { PromoCardDefaultWrapper } from './PromoCardDefaultWrapper'
import { GTM_ENV_VAR as GEV } from '../../lib/eventTrackerFunction'
import { AMP_EVENT_NAME } from '../../lib/amplitude'
import { useTrackPromoImpression } from '../BubbleCategoryCollection/trackingUtils'
import { IdleQueue } from '../../lib/idlize/IdleQueue'
import { trackPromotionCollectionEvent } from '../../lib/analytics/events/promo/PromotionCollectionEvent'

const AspectRatioBox = styled.div`
  position: relative;
  &:after {
    display: block;
    content: '';
    padding-bottom: 100%;
  }
  img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    padding: 0.5rem;
    object-fit: cover;
    object-position: top right;
  }
`
const Body = styled.div`
  padding: 0 8px 8px;
  font-size: 0.875rem;
  min-height: 5.25rem;
  border-bottom-left-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
  background-color: white;
`
const Title = styled.p`
  line-height: 1.25rem;
  max-height: 2.5rem;
  font-weight: 700;
  font-size: 0.875rem;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  /* stylelint-disable-next-line value-no-vendor-prefix */
  display: -webkit-box;
  /* stylelint-disable-next-line property-no-vendor-prefix */
  -webkit-line-clamp: 2;
  line-clamp: 2;
  /* stylelint-disable-next-line property-no-vendor-prefix */
  -webkit-box-orient: vertical;
`
const SubTitle = styled(Title)`
  color: #666;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1rem;
`

const Image = styled.img`
  width: 100%;
  height: 100%;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
`
const NoImgContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  height: 9.875rem;

  ${SCREEN_SIZE.From.Tablet} {
    height: 12.875rem;
  }
`

const queue = new IdleQueue()

const createImpressionTrackingData = ({
  checkoutAddress,
  position,
  collection,
  item,
}) => {
  const { id } = collection
  return {
    refId: `${checkoutAddress?.clientId}-${id}`,
    item_id: item?.publicUrl,
    item_name: item?.title,
    creative_name: item?.coverUrl,
    creative_slot: 'thumbnail',
    index: position,
  }
}
const PromoCard = ({
  item,
  gutter,
  visibleItems,
  checkoutAddress,
  wrapper: Wrapper = PromoCardDefaultWrapper,
  track,
  createObserver: createObserverInfo,
  bannerPosition,
}) => {
  const elementRef = useRef()
  const [isBrokenImage, setIsBrokenImage] = React.useState(false)
  const impressionTrackingData = createImpressionTrackingData({
    checkoutAddress,
    position: bannerPosition,
    collection: item,
    promoUrl: item?.publicUrl,
    title: item?.title,
    item,
  })
  const handleTrackClick = () => {
    trackPromotionCollectionEvent(true, {
      BUSINESS_VERTICAL: 'grocery',
      EVENT_CATEGORY: 'promotion collection',
      PROMOTION_COLLECTION_NAME: item?.title,
      PROMOTION_COLLECTION_POSITION: bannerPosition,
      STORE_ID_DBP: `${checkoutAddress.storeId}`,
      STORE_ID_SAP: checkoutAddress.clientId,
      STORE: checkoutAddress.storeName,
    })

    track({
      [GEV.EVENT]: 'select_promotion',
      [GEV.SCREEN_NAME]: 'grocery-online | weekly promotions | all',
      [GEV.CREATIVE_SLOT]: bannerPosition,
      [GEV.PROMOTION_ID]: 'grocery-online | weekly promotions | all',
      [GEV.PROMOTION_NAME]: 'listing | weekly promotions',
      [GEV.AMPLITUDE_CTA_LOCATION]: {
        pageName: 'weekly promotions',
        pageMeta: ['all'],
      },
      [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.PROMO_CLICKED,
      [GEV.AMPLITUDE_MESSAGE]: item?.title,
      [GEV.AMPLITUDE_ITEM_POSITIONS]: bannerPosition,
      [GEV.ITEMS]: [
        {
          item_id: item?.publicUrl,
          item_name: item?.title,
          creative_name: item?.coverUrl,
          creative_slot: 'thumbnail',
          index: bannerPosition,
        },
      ],
    })
  }
  useTrackPromoImpression({
    queue,
    elementRef,
    checkoutAddress,
    createObserverInfo,
    impressionTrackingData,
    collection: item,
    isCategory: false,
  })
  return (
    <Wrapper
      ref={elementRef}
      gutter={gutter}
      visibleItems={visibleItems}
      data-refid={`${checkoutAddress?.clientId}-${item.id}`}
      data-impressionType={impressionTypes.WEEKLY_PROMO_IMPRESSION}
      onClick={() => {}}
    >
      <Link href={item?.publicUrl} onClick={handleTrackClick}>
        {!isBrokenImage ? (
          <AspectRatioBox>
            <Image
              src={item?.coverUrl}
              alt={item.browserTitle}
              onError={() => {
                setIsBrokenImage(true)
              }}
            />
          </AspectRatioBox>
        ) : (
          <NoImgContainer>
            <NoImg width="50%" />
          </NoImgContainer>
        )}
        <Body>
          <Title>{item?.title}</Title>
          <SubTitle>{item?.description}</SubTitle>
        </Body>
      </Link>
    </Wrapper>
  )
}

const PromoCardWrp = props => (
  <CheckoutAddressConsumer>
    {({ checkoutAddress }) => (
      <EventTrackingConsumer>
        {({ track }) => (
          <PromoCard
            {...props}
            track={track}
            checkoutAddress={checkoutAddress}
          />
        )}
      </EventTrackingConsumer>
    )}
  </CheckoutAddressConsumer>
)

export default PromoCardWrp
